// bluaIQ — Diagnostic Takeoff v3 — SourceDocumentReviewPlaceholder
// Shell UI for source-document review. Captures only review state +
// reviewer notes. Does NOT capture quantities, parent boundaries,
// segment widths, opening quantities, or any measurement.

import { apiFetch } from '../../utils/api'
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FileSearch, Loader2 } from 'lucide-react'
import type {
  DiagnosticReviewStatus,
  DiagnosticSourceDocumentReview,
  SourceDocumentIdentity,
} from '../../takeoff-diagnostic-v3/types'

export interface SourceDocumentReviewPlaceholderProps {
  projectId: string
  document: SourceDocumentIdentity
  review: DiagnosticSourceDocumentReview | null
}

const STATUS_OPTIONS: DiagnosticReviewStatus[] = [
  'NOT_STARTED',
  'IN_PROGRESS',
  'BLOCKED',
  'CANDIDATE_REVIEWED',
]

const STATUS_LABEL: Record<DiagnosticReviewStatus, string> = {
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  BLOCKED: 'Blocked',
  CANDIDATE_REVIEWED: 'Candidate reviewed',
}

export default function SourceDocumentReviewPlaceholder({
  projectId,
  document,
  review,
}: SourceDocumentReviewPlaceholderProps) {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<DiagnosticReviewStatus>(
    (review?.status as DiagnosticReviewStatus) ?? 'NOT_STARTED',
  )
  const [notes, setNotes] = useState(review?.notes ?? '')

  useEffect(() => {
    setStatus((review?.status as DiagnosticReviewStatus) ?? 'NOT_STARTED')
    setNotes(review?.notes ?? '')
  }, [review?.id, review?.status, review?.notes])

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(
        `/api/takeoff-diagnostic/source-documents/${document.id}/review`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status, notes }),
        },
      )
      if (!res.ok) throw new Error('review-save-failed')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['diagnostic-takeoff-v3-snapshot', projectId],
      })
    },
  })

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3">
      <div className="flex items-start gap-2">
        <FileSearch size={16} className="text-[#9E9890] mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-[800] text-[#1C1A17] truncate">
            {document.originalFilename ?? 'Untitled source document'}
          </div>
          <div className="text-[10px] font-[600] text-[#9E9890] truncate">
            {document.contentHash ? `sha256: ${document.contentHash.slice(0, 16)}…` : 'no content hash yet'}
          </div>
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">
          Review status
        </label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as DiagnosticReviewStatus)}
          className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#1C1A17]"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1">
        <label className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">
          Reviewer notes (no quantities)
        </label>
        <textarea
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes for ChatGPT review handoff. Do not record measurements."
          className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#1C1A17]"
        />
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="bg-[#1C1A17] text-white rounded-full px-4 py-2 text-[11px] font-[800] disabled:opacity-40 inline-flex items-center gap-1.5"
        >
          {mutation.isPending && <Loader2 size={12} className="animate-spin" />}
          {mutation.isPending ? 'Saving…' : 'Save review'}
        </button>
      </div>

      {mutation.isError && (
        <div className="text-[11px] font-[700] text-red-600">
          Failed to save review.
        </div>
      )}
    </div>
  )
}
